import { keys } from 'ramda';
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import TEST_ID from './index.testid';

import KenmerkGroup from '../KenmerkGroup';
import type { RealworksRelatieSyncType } from '~/graphql/types';
import type {
  ActiveKenmerkSearch,
  Kenmerk,
  KenmerkLookup,
} from '../Synchronization/types';
import getKenmerkElementId from '~/components/page/Apps/Realworks/util/getKenmerkElementId';
import scrollElementIntoView from '~/util/scrollElementIntoView';

export type Props = {
  kenmerkLookup: KenmerkLookup;
  selectable?: boolean;
  syncType: RealworksRelatieSyncType;
  onChange: (args: { groupName: string; kenmerkList: Array<Kenmerk> }) => void;
  openGroupName: string | null;
  onOpenGroupChange: (groupName: string | null) => void;
  activeKenmerkSearch?: ActiveKenmerkSearch | null;
  /** Current kenmerk highlighted by the missing-kenmerken walkthrough wizard */
  activeWizardKenmerkId?: number | null;
  /** Kenmerk IDs not yet present in the saved kenmerkMapping */
  missingKenmerkIds?: Array<number>;
};

const RealworksSelectableAccordion: React.FCC<Props> = ({
  dataTestId,
  kenmerkLookup,
  onChange,
  syncType,
  selectable = true,
  openGroupName,
  onOpenGroupChange,
  activeKenmerkSearch = null,
  activeWizardKenmerkId = null,
  missingKenmerkIds = [],
  ...rest
}) => {
  const groupNames = keys(kenmerkLookup) as Array<string>;

  const handleToggle = (name: string) => {
    onOpenGroupChange(openGroupName === name ? null : name);
  };

  useEffect(() => {
    if (!activeKenmerkSearch || !openGroupName) return;

    void scrollElementIntoView(
      getKenmerkElementId(activeKenmerkSearch.kenmerkId),
      {
        initialDelay: 100,
      },
    );
  }, [activeKenmerkSearch, openGroupName]);

  return (
    <Container data-testid={dataTestId} {...rest}>
      {groupNames.map((name: string) => (
        <KenmerkGroup
          key={`key-${name}`}
          kenmerken={kenmerkLookup[name]}
          name={name}
          onChange={({ kenmerkList }) =>
            onChange({ groupName: name, kenmerkList })
          }
          dataTestId={TEST_ID.KENMERK_GROUP}
          selectable={selectable}
          syncType={syncType}
          isOpen={openGroupName === name}
          onToggle={() => handleToggle(name)}
          activeKenmerkSearch={activeKenmerkSearch}
          activeWizardKenmerkId={activeWizardKenmerkId}
          missingKenmerkIds={missingKenmerkIds}
        />
      ))}
    </Container>
  );
};

const Container = styled.div<{}>(
  ({ theme }) => css`
    width: 100%;
    ${theme.mq.lessThan('mobile')`
      margin: ${theme.space('l')} 0;
    `}
  `,
);

export default RealworksSelectableAccordion;
